import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Mic, MicOff, Send, CheckCircle, Loader2, AlertCircle, RotateCcw } from 'lucide-react'
import { api } from '../lib/api'

interface Message {
  role: 'agent' | 'patient'
  content: string
  timestamp: string
  quickReplies?: string[]
}

interface SessionInfo {
  session_id: string
  specialty: string
  doctor_name: string
  visit_reason: string | null
  requires_pin: boolean
  status: string
  greeting: string | null
}

export function IntakePage() {
  const { token } = useParams<{ token: string }>()
  const [session, setSession] = useState<SessionInfo | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [completing, setCompleting] = useState(false)
  // Phase 2: AI-scribe consent captured at intake (default checked = consents).
  const [aiConsent, setAiConsent] = useState(true)

  // PIN
  const [needsPin, setNeedsPin] = useState(false)
  const [pin, setPin] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [verifyingPin, setVerifyingPin] = useState(false)

  // Language
  const [language, setLanguage] = useState('en')

  // Voice
  const [isRecording, setIsRecording] = useState(false)
  const [recognition, setRecognition] = useState<any>(null)
  const [voiceSupported, setVoiceSupported] = useState(false)
  const [interimText, setInterimText] = useState('')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sending])

  // Web Speech API — continuous mode for better voice experience
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SR = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
      const recog = new SR()
      recog.continuous = false
      recog.interimResults = true
      recog.lang = language === 'es' ? 'es-US' : language === 'zh' ? 'zh-CN' : language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : language === 'tl' ? 'fil-PH' : language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-BR' : language === 'ru' ? 'ru-RU' : language === 'ht' ? 'ht-HT' : 'en-US'

      recog.onresult = (event: any) => {
        let interim = ''
        let final = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            final += t
          } else {
            interim += t
          }
        }
        if (final) {
          setInput(prev => (prev + ' ' + final).trim())
          setInterimText('')
        } else {
          setInterimText(interim)
        }
      }

      recog.onerror = () => setIsRecording(false)
      recog.onend = () => setIsRecording(false)

      setRecognition(recog)
      setVoiceSupported(true)
    }
  }, [])

  useEffect(() => {
    if (!token) { setError('Invalid link'); setLoading(false); return }
    loadSession()
  }, [token])

  const loadSession = async () => {
    try {
      setLoading(true)
      const data = await api.startIntakeSession(token!)
      setSession(data)
      setNeedsPin(data.requires_pin)
      if (!data.requires_pin && data.greeting) {
        showGreeting(data.greeting)
      }
    } catch (err: any) {
      setError(err.message || 'This link is no longer valid')
    } finally {
      setLoading(false)
    }
  }

  const showGreeting = (greeting: string) => {
    const parsed = parseQuickReplies(greeting)
    setMessages([{
      role: 'agent', content: parsed.text,
      quickReplies: parsed.quickReplies,
      timestamp: new Date().toISOString()
    }])
  }

  // Parse [quick reply] options from agent text
  const parseQuickReplies = (text: string): { text: string; quickReplies: string[] } => {
    // Look for lines like: [Option 1] [Option 2] [Option 3]
    const qrMatch = text.match(/\[([^\]]+)\]/g)
    if (qrMatch && qrMatch.length >= 2) {
      const replies = qrMatch.map(m => m.replace(/[\[\]]/g, ''))
      // Remove the quick reply line from the text
      const cleanText = text.replace(/\s*(\[[^\]]+\]\s*)+\s*$/, '').trim()
      return { text: cleanText, quickReplies: replies }
    }
    return { text, quickReplies: [] }
  }

  const verifyPin = async () => {
    if (!pin || pin.length < 4) { setPinError('Enter your 4-digit PIN'); return }
    try {
      setVerifyingPin(true); setPinError(null)
      await api.verifyIntakePin(token!, pin)
      setNeedsPin(false)
      if (session?.greeting) showGreeting(session.greeting)
    } catch (err: any) {
      setPinError('Wrong PIN — try again')
    } finally {
      setVerifyingPin(false)
    }
  }

  const sendMessage = async (text?: string, langOverride?: string) => {
    const msg = (text || input).trim()
    if (!msg || sending) return

    setMessages(prev => [...prev, { role: 'patient', content: msg, timestamp: new Date().toISOString() }])
    setInput('')
    setInterimText('')
    setSending(true)

    try {
      const response = await api.sendIntakeMessage(token!, msg, false, langOverride || language)
      const parsed = parseQuickReplies(response.response)
      setMessages(prev => [...prev, {
        role: 'agent', content: parsed.text,
        quickReplies: parsed.quickReplies,
        timestamp: new Date().toISOString()
      }])
      setProgress(response.progress_pct)
      if (response.is_complete) setIsComplete(true)
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: 'agent', content: "Sorry, I had a hiccup. Could you say that again?",
        timestamp: new Date().toISOString()
      }])
    } finally {
      setSending(false)
    }
  }

  const handleUndo = async () => {
    if (undoing || sending || messages.length < 2) return
    setUndoing(true)
    try {
      const res = await api.undoIntakeMessage(token!)
      // Drop the last patient + agent exchange locally
      setMessages(prev => {
        const next = [...prev]
        if (next.length && next[next.length - 1].role === 'agent') next.pop()
        if (next.length && next[next.length - 1].role === 'patient') next.pop()
        return next
      })
      if (typeof res.progress_pct === 'number') setProgress(res.progress_pct)
    } catch (err: any) {
      // Nothing to undo or session finished — ignore quietly
    } finally {
      setUndoing(false)
    }
  }

  const completeIntake = async () => {
    setCompleting(true)
    try {
      await api.completeIntakeSession(token!, aiConsent)
      setIsComplete(true)
    } catch (err: any) {
      setError(err.message || 'Failed to complete')
    } finally {
      setCompleting(false)
    }
  }

  const toggleRecording = () => {
    if (!recognition) return
    if (isRecording) {
      recognition.stop()
    } else {
      setIsRecording(true)
      try { recognition.start() } catch { /* already started */ }
    }
  }

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-brand-500 mx-auto mb-4" />
          <p className="text-lg text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Oops</h2>
          <p className="text-gray-500 text-lg">{error}</p>
        </div>
      </div>
    )
  }

  if (!session) return null

  // PIN screen — big digits, simple
  if (needsPin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔒</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-1">Enter Your PIN</h1>
            <p className="text-gray-500">For Dr. {session.doctor_name}</p>
          </div>

          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && verifyPin()}
            className="w-full px-4 py-4 text-3xl text-center tracking-[0.5em] rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-800 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none"
            placeholder="• • • •"
            autoFocus
          />
          {pinError && <p className="mt-3 text-center text-red-500 font-medium">{pinError}</p>}

          <button
            onClick={verifyPin}
            disabled={verifyingPin || pin.length < 4}
            className="w-full mt-6 py-4 bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 text-white rounded-xl font-semibold text-lg transition-colors"
          >
            {verifyingPin ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'Continue'}
          </button>
        </div>
      </div>
    )
  }

  // Completion screen
  if (isComplete && messages.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-12 h-12 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">All Done!</h2>
          <p className="text-lg text-gray-500 mb-6">
            Thank you! Dr. {session.doctor_name} will review this before your visit.
          </p>
          <div className="bg-indigo-50 rounded-xl p-4">
            <p className="text-brand-700 text-sm">
              You can close this page now. See you at your appointment! 👋
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Main conversation UI — big text, tap-friendly
  return (
    <div className="h-[100dvh] flex flex-col bg-gradient-to-b from-indigo-50 to-white">
      {/* Header — compact */}
      <header className="bg-white/80 backdrop-blur border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-800">Pre-Visit Intake</h1>
            <p className="text-sm text-gray-500">Dr. {session.doctor_name}</p>
          </div>
          <div className="flex items-center gap-3">
            {progress > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-500 transition-all duration-500 rounded-full"
                    style={{ width: `${progress}%` }} />
                </div>
                <span className="text-xs font-medium text-gray-400">{Math.round(progress)}%</span>
              </div>
            )}
            <select
              value={language}
              onChange={(e) => {
                const newLang = e.target.value
                setLanguage(newLang)
                if (messages.length > 0 && !sending) {
                  // Send a language switch message so the AI responds in the new language
                  sendMessage(`[Please continue in ${e.target.options[e.target.selectedIndex].text.replace(/^[^\s]+ /, '')}]`, newLang)
                }
              }}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              title="Language"
            >
              <option value="en">🇺🇸 English</option>
              <option value="es">🇪🇸 Español</option>
              <option value="zh">🇨🇳 中文</option>
              <option value="vi">🇻🇳 Tiếng Việt</option>
              <option value="ko">🇰🇷 한국어</option>
              <option value="tl">🇵🇭 Tagalog</option>
              <option value="ar">🇸🇦 العربية</option>
              <option value="fr">🇫🇷 Français</option>
              <option value="pt">🇧🇷 Português</option>
              <option value="ru">🇷🇺 Русский</option>
              <option value="ht">🇭🇹 Kreyòl</option>
            </select>
          </div>
        </div>
      </header>

      {/* Messages — large font, simple */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-lg mx-auto space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx}>
              {msg.role === 'agent' ? (
                <div className="space-y-3">
                  {/* Agent message — left aligned, large text */}
                  <div className="bg-white rounded-2xl px-5 py-4 shadow-sm border border-gray-100">
                    <p className="text-[17px] leading-relaxed text-gray-800 whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  
                  {/* Quick reply chips — only show on last agent message */}
                  {idx === messages.length - 1 && msg.quickReplies && msg.quickReplies.length > 0 && !sending && (
                    <div className="flex flex-wrap gap-2 px-1">
                      {msg.quickReplies.map((reply, i) => (
                        <button
                          key={i}
                          onClick={() => sendMessage(reply)}
                          className="px-4 py-2.5 bg-white border-2 border-brand-200 hover:border-brand-500 hover:bg-brand-50 
                            text-brand-700 rounded-full text-[15px] font-medium transition-all active:scale-95 shadow-sm"
                        >
                          {reply}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex justify-end">
                  <div className="bg-brand-500 text-white rounded-2xl px-5 py-3 max-w-[85%] shadow-sm">
                    <p className="text-[16px] leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {sending && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-400">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input — voice-first, big mic button */}
      <div className="bg-white border-t border-gray-200 px-4 py-4 flex-shrink-0 safe-area-inset-bottom">
        <div className="max-w-lg mx-auto">
          {/* Done button + AI-scribe consent. Reachable once a few questions are
              answered (NOT gated behind 60% completion) so the patient can always
              finish/exit and consent — even if the agent stops asking early
              (e.g. emergency triage to ER, or no further relevant questions). */}
          {messages.length >= 3 && !isComplete && (
            <>
              <label className="flex items-start gap-2 mb-3 px-1 text-left cursor-pointer">
                <input
                  type="checkbox"
                  checked={aiConsent}
                  onChange={(e) => setAiConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  I consent to my care team using an AI assistant to help take notes during my visits. You can change this at any time by telling your provider.
                </span>
              </label>
              <button
                onClick={completeIntake}
                disabled={completing}
                className="w-full mb-3 py-3 text-brand-600 hover:bg-brand-50 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
              >
                {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {progress >= 60 ? "I'm done" : 'Finish & submit'}
              </button>
              {progress < 60 && (
                <p className="text-center text-xs text-gray-400 mb-3 -mt-1">
                  You can finish anytime — your care team will see what you've shared so far.
                </p>
              )}
            </>
          )}

          {/* Voice recording indicator */}
          {isRecording && (
            <div className="mb-3 flex items-center gap-2 px-4 py-2 bg-red-50 rounded-xl">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-red-600 text-sm font-medium">Listening...</span>
              {interimText && <span className="text-red-400 text-sm italic truncate">{interimText}</span>}
            </div>
          )}

          {messages.length >= 3 && !isComplete && (
            <button
              onClick={handleUndo}
              disabled={undoing || sending}
              className="mb-2 flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
              title="Undo my last answer"
            >
              {undoing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              Undo last answer
            </button>
          )}

          <div className="flex items-end gap-2">
            {/* Voice button — big and prominent */}
            {voiceSupported && (
              <button
                onClick={toggleRecording}
                disabled={sending}
                className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center transition-all active:scale-95 ${
                  isRecording
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 animate-pulse'
                    : 'bg-brand-100 text-brand-600 hover:bg-brand-200'
                } disabled:opacity-50`}
                title={isRecording ? 'Stop' : 'Speak your answer'}
              >
                {isRecording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>
            )}

            {/* Text input */}
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }}}
                disabled={sending}
                placeholder="Type or tap an answer..."
                className="w-full px-4 py-3.5 rounded-2xl border-2 border-gray-200 bg-gray-50 text-gray-800 
                  focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none disabled:opacity-50 text-base"
              />
            </div>

            {/* Send */}
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || sending}
              className="flex-shrink-0 w-14 h-14 bg-brand-500 hover:bg-brand-600 disabled:bg-gray-200 
                text-white disabled:text-gray-400 rounded-2xl flex items-center justify-center transition-all active:scale-95"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>

          <p className="text-[11px] text-gray-400 mt-2 text-center">
            🔒 Your information is private and only shared with Dr. {session.doctor_name}.
            {' '}If this is an emergency, call 911.
          </p>
        </div>
      </div>
    </div>
  )
}
